/**
 * URL utility functions for generating proper article and page URLs
 */

import { getArticlePath, getArticleAbsoluteUrl as getArticleAbsUrl } from './article-url-utils';
import { Article } from '@/types/content';

// Default site URL used as fallback
const DEFAULT_SITE_URL = 'https://descout.xyz';

/**
 * Ensure a URL has a proper protocol (http:// or https://)
 * Validates the URL and falls back to default if invalid
 * 
 * Used for sitemap generation, feed URLs, and other places where
 * a valid absolute URL with protocol is required.
 * 
 * @param url - The URL to check and normalize
 * @param defaultUrl - Fallback URL if the input is invalid (defaults to site URL)
 * @returns URL with protocol, trailing slash removed
 */
export function ensureProtocol(url: string, defaultUrl: string = DEFAULT_SITE_URL): string {
  if (!url) return defaultUrl;
  
  // Trim whitespace
  let finalUrl = url.trim();
  
  // If URL already has protocol, clean it and validate
  if (finalUrl.startsWith('http://') || finalUrl.startsWith('https://')) {
    finalUrl = finalUrl.replace(/\/+$/, ''); // Remove trailing slash
    
    // Validate URL format
    try {
      new URL(finalUrl);
      return finalUrl;
    } catch {
      console.error(`Invalid URL: ${finalUrl}. Falling back to default.`);
      return defaultUrl;
    }
  }
  
  // Add https:// if missing
  finalUrl = `https://${finalUrl}`.replace(/\/+$/, '');
  
  // Validate the constructed URL
  try {
    new URL(finalUrl);
    return finalUrl;
  } catch {
    console.error(`Invalid URL constructed: ${finalUrl}. Falling back to default.`);
    return defaultUrl;
  }
}

// Get the site base URL from environment or default
export const getSiteUrl = (): string => {
  let url = process.env.NEXT_PUBLIC_SITE_URL || 'https://descout.xyz';
  
  // For development, detect if we're running locally
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    url = `${window.location.protocol}//${window.location.host}`;
  }
  
  // Trim whitespace
  url = url.trim();
  
  // Ensure URL has a protocol
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `https://${url}`;
  }
  
  // Remove trailing slash
  url = url.replace(/\/+$/, '');
  
  // Validate URL format
  try {
    new URL(url);
  } catch (e) {
    console.error(`Invalid NEXT_PUBLIC_SITE_URL: ${url}. Falling back to default.`);
    return 'https://descout.xyz';
  }
  
  return url;
};

// Generate article URL based on content type (backward compatible)
// For admin use - accepts either slug string or Article object
export const getArticleUrl = (slugOrArticle: string | Article | { slug: string; contentType?: 'news' | 'guide' | 'analysis' }): string => {
  if (typeof slugOrArticle === 'string') {
    // Legacy: Just a slug - default to news
    return `/news/${slugOrArticle}`;
  }
  // New: Article object with contentType
  return getArticlePath(slugOrArticle);
};

// Generate absolute article URL
export const getArticleAbsoluteUrl = (slugOrArticle: string | Article | { slug: string; contentType?: 'news' | 'guide' | 'analysis' }): string => {
  if (typeof slugOrArticle === 'string') {
    const siteUrl = getSiteUrl();
    return `${siteUrl}/news/${slugOrArticle}`;
  }
  return getArticleAbsUrl(slugOrArticle);
};

// Generate absolute URL for any path
export const getAbsoluteUrl = (path: string): string => {
  const siteUrl = getSiteUrl();
  
  // Handle empty path
  if (!path) return siteUrl;
  
  // If path is already a full URL, return it as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Ensure path starts with /
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  return `${siteUrl}${cleanPath}`;
};

// Get current page URL (for client-side components)
export const getCurrentUrl = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.href;
  }
  return getSiteUrl();
};

